import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours of the morning on the launch field just after sunrise, aloft in the
// clear mid-morning, and above the cloud sea in full sun.
const STOPS = {
  skyTop: ['#5A8BC9', '#3F7FCB', '#3570C0'],
  skyMiddle: ['#A6C4E2', '#8ABEEA', '#8DC2EC'],
  skyLow: ['#EBDCD2', '#D2E7F3', '#E6F0F0'],
  skyHorizon: ['#FFD7AC', '#F1F4EA', '#FFE7B8'],
  haze: ['#EADBD0', '#DCE8EE', '#F6E8D2'],
  farHill: ['#9AA9C8', '#93B0CB', '#B2BCCD'],
  nearHill: ['#748FA6', '#7598A9', '#94A3B4'],
  sunlight: ['#FFB27E', '#FFF0D2', '#FFD88E'],
  cloudLight: ['#FFF0E2', '#FFFFFF', '#FFF5DD'],
  cloudShade: ['#BCC1D9', '#BACCDF', '#E2C8B6'],
} as const;

type ColorName = keyof typeof STOPS;

export type BalloonsColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light at an altitude (0 on the launch field at sunrise, 1 above the clouds in full sun). */
export const balloonsColors = (altitude: number): BalloonsColors => {
  const scaled = clamp(altitude) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as BalloonsColors,
  );
};

/** The top of the morning sky stays a clear mid blue, so the header text stays white. */
export const balloonsChromeTone = (progress: number): ChromeTone =>
  luminance(balloonsColors(progress).skyTop) > 0.31 ? 'dark' : 'light';
